import { flagEmoji } from "@shared/flag";
import { apiLeaderboard, type LeaderboardEntry } from "../api";

export interface LeaderboardHandle {
  open(): void;
  close(): void;
  refresh(): Promise<void>;
  dispose(): void;
  onHover?: (handle: string | null) => void;
}

export function mountLeaderboard(
  panel: HTMLElement,
  openBtn: HTMLElement,
  closeBtn: HTMLElement,
  opts: { courseDate: string; myHandle: string },
): LeaderboardHandle {
  const body = panel.querySelector<HTMLElement>("#leaderboard-body")!;

  let lastFetch = 0;

  let handleObj: LeaderboardHandle;

  const render = (entries: LeaderboardEntry[]) => {
    if (entries.length === 0) {
      body.innerHTML = `<div class="panel-empty">No scores yet today. Be the first.</div>`;
      return;
    }
    body.innerHTML = "";
    entries.forEach((e, i) => {
      const isYou = e.handle === opts.myHandle;
      const you = isYou ? " you" : "";
      const rank = i + 1;
      const rankGlyph =
        rank === 1 ? "👑" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : String(rank);
      // Gradient palette rotates by first char so a given handle's
      // avatar stays stable across refreshes.
      const hash =
        (e.handle.charCodeAt(0) ?? 0) + (e.handle.charCodeAt(1) ?? 0);
      const hue = (hash % 4) + 1;
      const initial = (e.handle[0] ?? "?").toUpperCase();
      const div = document.createElement("div");
      div.className = `lb-row${you}`;
      div.innerHTML = `
        <div class="rank">${rankGlyph}</div>
        <div class="avatar avatar-h${hue}">
          ${escapeHtml(initial)}
          <span class="flag">${escapeHtml(flagEmoji(e.countryCode))}</span>
        </div>
        <div class="handle">${escapeHtml(e.handle)}</div>
        <div class="strokes">${e.totalStrokes}</div>
      `;
      div.addEventListener("mouseenter", () => {
        if (handleObj.onHover) handleObj.onHover(e.handle);
      });
      div.addEventListener("mouseleave", () => {
        if (handleObj.onHover) handleObj.onHover(null);
      });
      body.appendChild(div);
    });
  };

  const refresh = async () => {
    const tick = ++lastFetch;
    body.innerHTML = `<div class="panel-empty">Loading…</div>`;
    try {
      const res = await apiLeaderboard(opts.courseDate, 50);
      if (tick !== lastFetch) return;
      render(res.entries ?? []);
    } catch {
      if (tick !== lastFetch) return;
      body.innerHTML = `<div class="panel-empty">Couldn't load leaderboard.</div>`;
    }
  };

  const open = () => {
    panel.hidden = false;
    requestAnimationFrame(() => panel.classList.add("open"));
    void refresh();
  };
  const close = () => {
    panel.classList.remove("open");
    setTimeout(() => {
      if (!panel.classList.contains("open")) panel.hidden = true;
    }, 300);
  };

  const onOpenClick = () => open();
  const onCloseClick = () => close();
  openBtn.addEventListener("click", onOpenClick);
  closeBtn.addEventListener("click", onCloseClick);

  handleObj = {
    open,
    close,
    refresh,
    dispose() {
      openBtn.removeEventListener("click", onOpenClick);
      closeBtn.removeEventListener("click", onCloseClick);
    },
  };
  return handleObj;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
